import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { MapPin, Copy, Navigation2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Kayıtlı koordinatları harici navigasyon uygulamalarında açar.
 * Google Maps API anahtarı gerektirmez — sadece coord-based deep links.
 */
const NavigationDialog = ({ open, onClose, lat, lng, title }) => {
  const isApple = /iPhone|iPad|iPod|Mac/i.test(navigator.userAgent);

  const links = {
    apple: `https://maps.apple.com/?daddr=${lat},${lng}&q=${encodeURIComponent(title || "Hedef")}`,
    google: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
    waze: `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`,
  };

  const openLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  const copyCoords = async () => {
    const text = `${lat}, ${lng}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Koordinat kopyalandı");
    } catch (e) {
      toast.error("Kopyalama başarısız");
    }
    onClose();
  };

  if (!lat || !lng) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="navigation-dialog"
        className="max-w-sm bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]"
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center mb-2">
            <Navigation2 className="w-6 h-6 text-white" />
          </div>
          <DialogTitle className="text-[#0A0A0A] dark:text-[#FAFAFA]">
            Yol Tarifi
          </DialogTitle>
          <p className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
            {title || "Hedef"} • {lat.toFixed(5)}, {lng.toFixed(5)}
          </p>
        </DialogHeader>

        <div className="space-y-2 mt-4">
          {isApple && (
            <NavButton
              testId="nav-apple-maps"
              onClick={() => openLink(links.apple)}
              icon={<MapPin className="w-5 h-5" />}
              label="Apple Haritalar'da Aç"
              accent="#0A0A0A"
            />
          )}
          <NavButton
            testId="nav-google-maps"
            onClick={() => openLink(links.google)}
            icon={<MapPin className="w-5 h-5" />}
            label="Google Maps'te Aç"
            accent="#4285F4"
          />
          <NavButton
            testId="nav-waze"
            onClick={() => openLink(links.waze)}
            icon={<Navigation2 className="w-5 h-5" />}
            label="Waze'de Aç"
            accent="#33CCFF"
          />
          <NavButton
            testId="nav-copy-coords"
            onClick={copyCoords}
            icon={<Copy className="w-5 h-5" />}
            label="Koordinatı Kopyala"
            accent="#F59E0B"
            variant="outline"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NavButton = ({ testId, onClick, icon, label, accent, variant }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-150 ${
      variant === "outline"
        ? "border border-[#E4E4E7] dark:border-[#27272A] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A] text-[#0A0A0A] dark:text-[#FAFAFA]"
        : "text-white hover:brightness-110"
    }`}
    style={variant === "outline" ? {} : { backgroundColor: accent }}
  >
    <span className={variant === "outline" ? "" : "text-white"}>{icon}</span>
    <span className="font-semibold flex-1">{label}</span>
    {variant !== "outline" && <ExternalLink className="w-4 h-4 opacity-70" />}
  </button>
);

export default NavigationDialog;
